import { FormattedMessage, injectIntl } from 'react-intl';
import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp, faTimes } from '@fortawesome/free-solid-svg-icons';
import utils from '../../../utils/utils';
import DATE_FORMAT from '../../../components/constants/dateFormat';
import RouteForm from './routeForm';
import DeliveryTable from './deliveryTable';
import ROUTE_STATUS from '../../../components/constants/routeStatus';
import { useRoute } from '../apiHooks';

const RouteTable = ({ route, selectedDeliveryDay, routePlan, routes, compact, deliverySearchResults }) => {
  const [showDetails, setShowDetails] = useState(!compact);
  const [fetchWithContent, setFetchWithContent] = useState(false);
  const [editRoute, setEditRoute] = useState(null);

  useEffect(() => {
    setShowDetails(!compact);
  }, [compact]);

  const { route: routeWithContent, isFetching } = useRoute({
    routeId: route.id,
    showOrderInfo: true,
    enabled: fetchWithContent,
  });

  const contentRoute = routeWithContent || route; // use a different route for displaying

  const combinedCustomTypes = contentRoute.deliveries.reduce((result, delivery) => {
    if (!delivery.deliveryOrderBatch || !delivery.deliveryOrderBatch.customTypes) {
      return result;
    }

    delivery.deliveryOrderBatch.customTypes.forEach((customType) => {
      const existingCustomTypeIndex = result.findIndex((ct) => ct.customType.type === customType.type);
      if (existingCustomTypeIndex === -1) {
        result.push({
          customType,
          quantity: customType.quantity,
        });
      } else {
        result[existingCustomTypeIndex].quantity += customType.quantity;
      }
    });

    return result;
  }, []);

  const statuses = Object.keys(ROUTE_STATUS).map((key) => {
    return ROUTE_STATUS[key];
  });
  const status = statuses.find((s) => s.name === route.status);

  return (
    <div key={route.id} className={`${showDetails ? 'col-12' : 'col-6 col-sm-4'} m-b-6`}>
      <h4 className="m-b-1">
        {route.driver ? (
          <FormattedMessage
            id={'admin.route.planning.edit.route.header.with.driver'}
            values={{ routeId: route.id, driver: route.driver.name }}
          />
        ) : (
          <FormattedMessage id={'admin.route.planning.edit.route.header.no.driver'} values={{ routeId: route.id }} />
        )}{' '}
      </h4>
      {route.isFinal ? (
        <div>
          <FormattedMessage id={'admin.route.planning.edit.route.header.final'} />
        </div>
      ) : (
        ''
      )}

      <div className="m-b-1">
        {compact ? (
          <button onClick={() => setShowDetails(!showDetails)} className="btn btn-normal-link m-r-2">
            <small>
              <FontAwesomeIcon icon={showDetails ? faCaretUp : faCaretDown} />{' '}
              <FormattedMessage id={'admin.route.planning.edit.route.show.details'} />
            </small>
          </button>
        ) : (
          ''
        )}
        <button onClick={() => setEditRoute(route)} className="btn btn-normal-link m-r-2">
          <small>
            <FormattedMessage id={'admin.route.planning.edit.route.edit'} />
          </small>
        </button>
      </div>
      <div className="m-b-3">
        {showDetails ? (
          <>
            {route.startDepot ? (
              <div>
                <strong>
                  <FormattedMessage id={'admin.route.planning.edit.route.start.depot.label'} />
                </strong>
                : {route.startDepot.street}
              </div>
            ) : (
              ''
            )}
            <div>
              <strong>
                <FormattedMessage id={'admin.route.planning.edit.route.duration.label'} />
              </strong>
              :{' '}
              {route.durationInMinutes ? (
                <FormattedMessage
                  id={'admin.route.planning.edit.route.duration.value'}
                  values={{ value: utils.getRoundedPrice(route.durationInMinutes) }}
                />
              ) : (
                '-'
              )}
            </div>
            <div>
              <strong>
                <FormattedMessage id={'admin.route.planning.edit.route.distance.label'} />
              </strong>
              :{' '}
              {route.distanceInMeters ? (
                <FormattedMessage
                  id={'admin.route.planning.edit.route.distance.value'}
                  values={{ value: utils.getRoundedPrice(route.distanceInMeters / 1000) }}
                />
              ) : (
                '-'
              )}
            </div>
          </>
        ) : (
          ''
        )}
        <div>
          <strong>
            <FormattedMessage id={'admin.route.planning.edit.route.delivery.count.label'} />
          </strong>
          : {route.deliveries?.length}
        </div>
        <div>
          <strong>
            <FormattedMessage id={'admin.route.planning.edit.route.status.label'} />
          </strong>
          : {status ? `${status.icon} ${status.name}` : route.status}
        </div>
        <div>
          <strong>
            <FormattedMessage id={'admin.route.planning.edit.route.packer.label'} />
          </strong>
          : {route.packer ? `${route.packer.screenName}` : '-'}
        </div>
        <div>
          <strong>
            <FormattedMessage id={'admin.route.planning.edit.route.start.time.label'} />
          </strong>
          : {route.startTime ? utils.getDateFromString(route.startTime).format(DATE_FORMAT.displayWithTime) : '-'}
        </div>
        {route.routeOptimisationResult &&
        ['IN_QUEUE', 'IN_PROGRESS', 'FAILED'].indexOf(route.routeOptimisationResult.status) !== -1 ? (
          <div>
            <strong>
              <FormattedMessage id={'admin.route.planning.edit.route.optimisation.result.label'} />
            </strong>
            : <span className="text-warning">{route.routeOptimisationResult.status}</span>
          </div>
        ) : (
          ''
        )}
        {showDetails ? (
          <>
            <hr className="m-t-3 m-b-3" />
            {contentRoute.orderInfoHidden ? (
              <div>
                <button
                  className={`btn btn-default btn-small ${isFetching ? 'btn-loading' : ''}`}
                  disabled={isFetching}
                  onClick={() => setFetchWithContent(true)}
                >
                  <FormattedMessage id={'admin.route.planning.edit.route.get.order.info'} />
                </button>
              </div>
            ) : (
              <div>
                {combinedCustomTypes.map((combinedCustomType) => (
                  <div key={combinedCustomType.customType.label}>
                    {combinedCustomType.customType.icon} {combinedCustomType.customType.label}:{' '}
                    <strong>{combinedCustomType.customType.showQuantity ? combinedCustomType.quantity : '✅'}</strong>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          ''
        )}
      </div>
      {showDetails ? (
        <DeliveryTable
          selectedDeliveryDay={selectedDeliveryDay}
          routePlan={routePlan}
          routes={routes}
          routeId={route.id}
          deliveries={route.deliveries}
          deliveriesWithContent={routeWithContent?.deliveries}
          searchResults={deliverySearchResults}
        />
      ) : (
        ''
      )}

      <Modal show={!!editRoute} onHide={() => setEditRoute(null)} size="md">
        <div onClick={() => setEditRoute(null)} className="b-modal-close">
          <FontAwesomeIcon icon={faTimes} />
        </div>

        <div className="b-modal">
          {editRoute ? (
            <RouteForm
              route={editRoute}
              onSuccess={() => {
                setEditRoute(null);
              }}
            />
          ) : (
            ''
          )}
        </div>
      </Modal>
    </div>
  );
};

export default injectIntl(RouteTable);
